import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../../data/portfolioWork";
import { trackAction } from "./PageAnalytics";

// Copies the address and says so. Both labels, and both icons, are laid in
// the same grid cell, so "Copied" never changes the button's layout width and
// nothing beside it moves. On the pills the painted capsule narrows instead,
// by the same amount from each side (--pf-copy-shrink, half the difference
// between the two labels), and the content slides to stay centred in it; the
// labels cross-fade with a small scale, so each one grows into place. Focus
// stays on the button throughout; the confirmation is announced through a
// polite live region.
//
// When the Clipboard API is missing or refuses (insecure context, denied
// permission, older browsers), the address is shown and selected so the reader
// can copy it by hand, and the mailto link is right beside it.
//
// `primary` is the intro's dark pill, `pill` the shared grey secondary action
// and `quiet` a text-weight button for rows of plain links, such as the footer.

const RESET_MS = 2000;

const VARIANT_CLASS = {
  primary: "pf-pill pf-pill--primary",
  pill: "pf-pill",
  quiet: "pf-quiet-button",
};

function selectContents(node) {
  if (!node || typeof window.getSelection !== "function") return;
  const range = document.createRange();
  range.selectNodeContents(node);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function CopyEmailButton({ variant = "pill", source, className = "" }) {
  const [state, setState] = useState("idle");
  const [announcement, setAnnouncement] = useState("");
  const addressRef = useRef(null);
  const buttonRef = useRef(null);
  const copyLabelRef = useRef(null);
  const copiedLabelRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  // offsetWidth ignores the labels' scale, so this reads their resting size.
  useLayoutEffect(() => {
    const measure = () => {
      const button = buttonRef.current;
      if (!button || !copyLabelRef.current || !copiedLabelRef.current) return;
      const shrink = (copyLabelRef.current.offsetWidth - copiedLabelRef.current.offsetWidth) / 2;
      button.style.setProperty("--pf-copy-shrink", `${Math.max(0, shrink)}px`);
    };
    measure();
    document.fonts?.ready.then(measure);
  }, []);

  useEffect(() => {
    if (state === "failed") selectContents(addressRef.current);
  }, [state]);

  const copy = async () => {
    clearTimeout(timer.current);
    trackAction("copy_email", source);
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setState("copied");
      setAnnouncement("Email address copied.");
      timer.current = setTimeout(() => {
        setState("idle");
        setAnnouncement("");
      }, RESET_MS);
    } catch (error) {
      setState("failed");
      setAnnouncement(
        `Could not copy automatically. The address ${CONTACT_EMAIL} is shown and selected.`
      );
    }
  };

  const copied = state === "copied";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={copy}
        className={`${VARIANT_CLASS[variant] || VARIANT_CLASS.pill} pf-copy-email${
          copied ? " is-copied" : ""
        } ${className}`.trim()}
      >
        <span className="pf-copy-email__content">
          <span className="pf-copy-email__swap" aria-hidden="true">
            <Copy size={16} strokeWidth={1.75} className={copied ? "is-hidden" : undefined} />
            <Check size={16} strokeWidth={2} className={copied ? undefined : "is-hidden"} />
          </span>
          <span className="pf-copy-email__swap pf-copy-email__labels">
            <span ref={copyLabelRef} className={copied ? "is-hidden" : undefined} aria-hidden={copied}>
              Copy email
            </span>
            <span ref={copiedLabelRef} className={copied ? undefined : "is-hidden"} aria-hidden={!copied}>
              Copied
            </span>
          </span>
        </span>
      </button>
      <span className="pf-visually-hidden" aria-live="polite">
        {announcement}
      </span>
      {state === "failed" && (
        <p className="pf-copy-email__fallback">
          <span>Copying is blocked here. Select the address and copy it:</span>{" "}
          <span ref={addressRef} className="pf-copy-email__address">
            {CONTACT_EMAIL}
          </span>{" "}
          or{" "}
          <a href={CONTACT_MAILTO} onClick={() => trackAction("mailto", "copy_fallback")}>
            open your email app
          </a>
          .
        </p>
      )}
    </>
  );
}

export default CopyEmailButton;
